import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FormContext,
  FormContextProvider,
  FormSchema,
  SchemaFieldsWithContextUI,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { LayoutTypes } from '@stormwater-software/lib-inspection-templates';
import { Button } from '@stormwater-software/lib-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import InspectionQuestionContext, {
  InspectionQuestionProvider,
} from '../../../contexts/InspectionQuestionContext';
import QuestionGroupContext, {
  EditorQuestionGroup,
  QuestionGroupProvider,
} from '../../../contexts/QuestionGroupContext';
import TemplateEditorContext from '../../../contexts/TemplateEditorContext';
import { useUpdateQuestionGroup } from '../../../hooks/questionGroups';
import { PdfLayoutGroup, PdfLayoutSection } from '../../../utils/api/pdfLayout';
import { questionApi } from '../../../utils/api/question';
import { questionGroupApi } from '../../../utils/api/questionGroup';
import Loading from '../../Shared/ResourceIndex/Loading';
import SectionStyleControls from '../PdfLayout/SectionStyleControls';
import DeleteConfirmModal from './Forms/DeleteConfirmModal';
import GridQuestionDetailForm from './Forms/GridQuestionDetailForm';
import QuestionDetailForm from './Forms/QuestionDetailForm';
import GridLayoutPreview from './GridLayoutPreview';
import InspectionQuestions from './InspectionQuestions';

interface IEditQuestionGroupFormProps {
  questionGroup: EditorQuestionGroup;
  onClose: () => void;
}

const EditQuestionGroupForm: React.FC<IEditQuestionGroupFormProps> = ({
  questionGroup,
  onClose,
}) => {
  const { templateId } = useContext(TemplateEditorContext);
  const updateMutation = useUpdateQuestionGroup(templateId);

  const handleSubmit = (
    name: string,
    description: string,
    layout_type: LayoutTypes,
  ) => {
    if (layout_type === LayoutTypes.FORM) {
      questionGroup.questions?.forEach((question) => {
        if (question.features) {
          question.features = question.features.filter(
            (feature) => feature !== 'comment',
          );
        }
      });
    }
    updateMutation.mutate({
      ...questionGroup,
      name,
      layout_type,
      description,
      table_headers: questionGroup.table_headers || [],
    });
    onClose();
  };

  const schema: FormSchema = {
    name: {
      validation: { required: true },
      className: 'question-group-name-input',
    },
    description: {
      controlType: UIControlType.text,
      placeholder: 'Optional description',
      validation: { required: false },
      className: 'question-group-description-input',
    },
    layout_type: {
      controlType: UIControlType.select,
      options: [
        { value: 'FORM', label: 'Form Layout' },
        { value: 'TABLE', label: 'Table Layout' },
      ],
      labelKey: 'label',
      valueKey: 'value',
      validation: { required: true },
      style: { width: 'fit-content' },
    },
  };

  return (
    <FormContextProvider>
      <FormContext.Consumer>
        {(formContext) => (
          <div className='question-group-name-form'>
            <SchemaFieldsWithContextUI
              className='question-group-name-form-inputs'
              schema={schema}
              initialFormData={{
                name: questionGroup.name,
                description: questionGroup.description,
                layout_type: questionGroup.layout_type,
              }}
            />
            <div className='question-group-name-form-controls'>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                primary
                onClick={() =>
                  handleSubmit(
                    formContext.value.name,
                    formContext.value.description,
                    formContext.value.layout_type,
                  )
                }
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

/** "+ Add question" — opens the new-question form. Lives inside the provider. */
const AddQuestion: React.FC<{ order: number }> = ({ order }) => {
  const inspectionQuestionContext = useContext(InspectionQuestionContext);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (newQuestion: any) => {
    setLoading(true);
    try {
      await inspectionQuestionContext.addQuestion(newQuestion);
    } finally {
      setShowForm(false);
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <button
        type='button'
        className='utb-chip utb-chip--action'
        onClick={() => setShowForm(true)}
      >
        + Add question
      </button>
      {showForm && (
        <QuestionDetailForm
          onCancel={() => setShowForm(false)}
          onSubmit={handleSubmit}
          title='Add a Question'
          order={order}
        />
      )}
    </>
  );
};

export interface IQuestionGroupsProps {
  group: PdfLayoutGroup;
  index: number;
  count: number;
  section: PdfLayoutSection;
  pageBreakAfter: boolean;
  onChangeSection: (fields: Partial<PdfLayoutSection>) => void;
  onTogglePageBreak: (on: boolean) => void;
}

const GroupCard: React.FC<IQuestionGroupsProps> = ({
  group,
  index,
  count,
  section,
  pageBreakAfter,
  onChangeSection,
  onTogglePageBreak,
}) => {
  const templateEditorContext = useContext(TemplateEditorContext);
  const { questionGroup } = useContext(QuestionGroupContext);
  const [collapsed, setCollapsed] = useState(true);
  const [edit, setEdit] = useState(false);
  const [showStyle, setShowStyle] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showGridForm, setShowGridForm] = useState(false);
  const [isUsedTemplate, setIsUsedTemplate] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the mobile action menu when clicking away from it.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);

    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  const isGrid = group.layout_type === 'GRID';
  const isConditionGroup = group.name === 'Condition Group';
  const nextQuestionOrder =
    group.questions.length > 0
      ? Math.max(...group.questions.map((q) => q.sort_order)) + 1
      : 0;

  const openGridForm = async () => {
    const firstQuestion = group.questions[0];

    try {
      setIsUsedTemplate(
        firstQuestion
          ? await questionApi.getIsUsedTemplate(firstQuestion.id)
          : false,
      );
    } catch {
      setIsUsedTemplate(false);
    }
    setShowGridForm(true);
  };

  // Single source of truth for the header actions: rendered as inline icon
  // buttons on desktop and folded into a kebab menu below 640px.
  const actions: Array<{
    key: string;
    title: string;
    icon: any;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
    active?: boolean;
  }> = [];

  if (!isConditionGroup) {
    actions.push(
      {
        key: 'up',
        title: 'Move up',
        icon: 'chevron-up',
        disabled: index === 0,
        onClick: () => templateEditorContext.swapQuestionGroups(group.id, 'up'),
      },
      {
        key: 'down',
        title: 'Move down',
        icon: 'chevron-down',
        disabled: index === count - 1,
        onClick: () =>
          templateEditorContext.swapQuestionGroups(group.id, 'down'),
      },
      {
        key: 'style',
        title: 'Section styling',
        icon: 'palette',
        active: showStyle,
        onClick: () => setShowStyle((v) => !v),
      },
    );
  }

  if (isGrid) {
    actions.push({
      key: 'grid',
      title: 'Edit grid',
      icon: 'table',
      onClick: openGridForm,
    });
  } else if (!isConditionGroup) {
    actions.push({
      key: 'edit',
      title: 'Edit group',
      icon: 'pen',
      onClick: () => setEdit(true),
    });
  }

  actions.push(
    {
      key: 'delete',
      title: 'Delete group',
      icon: 'trash',
      danger: true,
      onClick: () => setShowDelete(true),
    },
    {
      key: 'collapse',
      title: collapsed ? 'Expand' : 'Collapse',
      icon: collapsed ? 'caret-down' : 'caret-up',
      onClick: () => setCollapsed((v) => !v),
    },
  );

  return (
    <div className='utb-group'>
      <div className='utb-group__header'>
        {!edit ? (
          <>
            <span className='utb-group__badge'>
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3
              className='utb-group__name'
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button')) return;
                setCollapsed((v) => !v);
              }}
            >
              {group.name}
            </h3>
            <span className='utb-chip'>{group.questions.length} fields</span>
            <span className='utb-group__actions'>
              {actions.map((action) => (
                <button
                  key={action.key}
                  type='button'
                  title={action.title}
                  disabled={action.disabled}
                  className={
                    `${action.danger ? 'is-danger' : ''} ${
                      action.active ? 'is-active' : ''
                    }`.trim() || undefined
                  }
                  onClick={action.onClick}
                >
                  <FontAwesomeIcon icon={action.icon} fixedWidth />
                </button>
              ))}
            </span>
            <div className='utb-group__menu' ref={menuRef}>
              <button
                type='button'
                className='utb-group__menu-toggle'
                title='Actions'
                aria-haspopup='true'
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <FontAwesomeIcon icon='ellipsis-v' fixedWidth />
              </button>
              {menuOpen && (
                <ul className='utb-group__menu-list'>
                  {actions.map((action) => (
                    <li key={action.key}>
                      <button
                        type='button'
                        disabled={action.disabled}
                        className={
                          `${action.danger ? 'is-danger' : ''} ${
                            action.active ? 'is-active' : ''
                          }`.trim() || undefined
                        }
                        onClick={() => {
                          action.onClick();
                          setMenuOpen(false);
                        }}
                      >
                        <FontAwesomeIcon icon={action.icon} fixedWidth />
                        <span>{action.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <EditQuestionGroupForm
            questionGroup={questionGroup}
            onClose={() => setEdit(false)}
          />
        )}
      </div>

      {showStyle && (
        <div className='utb-group__style'>
          <SectionStyleControls
            section={section}
            layoutType={group.layout_type}
            columns={group.table_headers}
            onChange={onChangeSection}
          />
        </div>
      )}

      {!collapsed && (
        <InspectionQuestionProvider initialValue={{ groupId: group.id }}>
          {isGrid ? <GridLayoutPreview /> : <InspectionQuestions />}

          <div className='utb-group__footer'>
            {!isGrid && !isConditionGroup && (
              <AddQuestion order={nextQuestionOrder} />
            )}
            <button
              type='button'
              className={`utb-chip utb-chip--toggle ${pageBreakAfter ? 'is-on' : ''}`}
              onClick={() => onTogglePageBreak(!pageBreakAfter)}
            >
              ✂ Page break after
            </button>
          </div>
        </InspectionQuestionProvider>
      )}

      {showGridForm && (
        <GridQuestionDetailForm
          isUsedTemplate={isUsedTemplate}
          onCancel={() => setShowGridForm(false)}
          onSubmit={() => {
            setShowGridForm(false);
            templateEditorContext.refetchQuestionGroups();
          }}
        />
      )}

      <DeleteConfirmModal
        show={showDelete}
        title='Delete Question Group'
        message={`Are you sure that you want to delete the Question Group "${group.name}"?`}
        onClose={() => setShowDelete(false)}
        onConfirm={async () => {
          await questionGroupApi.delete(
            templateEditorContext.templateId,
            group.id,
          );
          setShowDelete(false);
          templateEditorContext.refetchQuestionGroups();
        }}
      />
    </div>
  );
};

const QuestionGroups: React.FC<IQuestionGroupsProps> = (props) => (
  <QuestionGroupProvider group={props.group}>
    <GroupCard {...props} />
  </QuestionGroupProvider>
);

export default QuestionGroups;
